import { create } from "zustand";
import type { SourceSegment } from "../types/sop";

export interface FocusedStepSegment {
  videoId: string;
  segment: SourceSegment;
}

export type ReferencePanelTab = "preview" | "suggestions";

interface SopPreviewState {
  selectedFileId: string;
  videoTimestamps: Record<string, number>;
  focusedSegment: FocusedStepSegment | null;
}

interface DocumentPreviewState {
  bySopId: Record<string, SopPreviewState>;
  /** Right aside: collapsed state so toolbar can open it */
  asideCollapsed: boolean;
  asideActiveTab: ReferencePanelTab;
  /** Step key (sopId:versionId:stepIndex) when opening Suggestions tab from a step */
  suggestionsStepKey: string | null;
}

interface DocumentPreviewActions {
  getSelectedFileId: (sopId: string) => string;
  setSelectedFileId: (sopId: string, fileId: string) => void;
  getVideoTimestamp: (sopId: string, mediaId: string) => number | undefined;
  setVideoTimestamp: (sopId: string, mediaId: string, time: number) => void;
  setFocusedSegment: (
    sopId: string,
    segment: FocusedStepSegment | null,
  ) => void;
  setAsideCollapsed: (collapsed: boolean) => void;
  setAsideActiveTab: (tab: ReferencePanelTab) => void;
  /** Open the aside, switch to Suggestions tab, and set step context for the list */
  openSuggestionsPanel: (stepKey: string) => void;
}

function getSopState(
  state: DocumentPreviewState,
  sopId: string,
): SopPreviewState {
  return (
    state.bySopId[sopId] ?? {
      selectedFileId: "",
      videoTimestamps: {},
      focusedSegment: null,
    }
  );
}

export const useDocumentPreviewStore = create<
  DocumentPreviewState & DocumentPreviewActions
>()((set, get) => ({
  bySopId: {},
  asideCollapsed: false,
  asideActiveTab: "preview",
  suggestionsStepKey: null,

  getSelectedFileId: (sopId: string) => {
    return getSopState(get(), sopId).selectedFileId;
  },

  setSelectedFileId: (sopId: string, fileId: string) => {
    set((state) => ({
      bySopId: {
        ...state.bySopId,
        [sopId]: {
          ...getSopState(state, sopId),
          selectedFileId: fileId,
        },
      },
    }));
  },

  getVideoTimestamp: (sopId: string, mediaId: string) => {
    const timestamps = getSopState(get(), sopId).videoTimestamps[mediaId];
    return timestamps;
  },

  setVideoTimestamp: (sopId: string, mediaId: string, time: number) => {
    set((state) => {
      const sop = getSopState(state, sopId);
      return {
        bySopId: {
          ...state.bySopId,
          [sopId]: {
            ...sop,
            videoTimestamps: {
              ...sop.videoTimestamps,
              [mediaId]: time,
            },
          },
        },
      };
    });
  },

  setFocusedSegment: (
    sopId: string,
    segment: FocusedStepSegment | null,
  ) => {
    set((state) => ({
      bySopId: {
        ...state.bySopId,
        [sopId]: {
          ...getSopState(state, sopId),
          focusedSegment: segment,
        },
      },
    }));
  },

  setAsideCollapsed: (collapsed: boolean) => {
    set({ asideCollapsed: collapsed });
  },

  setAsideActiveTab: (tab: ReferencePanelTab) => {
    set({ asideActiveTab: tab });
  },

  openSuggestionsPanel: (stepKey: string) => {
    set({
      asideCollapsed: false,
      asideActiveTab: "suggestions",
      suggestionsStepKey: stepKey,
    });
  },
}));
